import { Traveller } from '../../../../requests-module'
import { calculateAge } from '@/utils/dateUtilts'

const STORAGE_KEY = 'bf_participants'
const TTL_DAYS = 365
const ADULT_AGE_THRESHOLD = 18

export interface SavedParticipantData {
    firstName: string
    lastName: string
    birthday: string
    gender: string | null
}

export interface AdultChildCounts {
    adultCount: number
    childCount: number
}

export interface SavedParticipantsEntry {
    count: number
    adultCount: number
    childCount: number
    /** @deprecated legacy entries; derived from ages when adultCount/childCount missing */
    ages?: number[]
    travellers: SavedParticipantData[]
    updatedAt: string
}

const isStorageAvailable = (): boolean => typeof Storage !== 'undefined'

export const isAdultAge = (age: number): boolean => age >= ADULT_AGE_THRESHOLD

export const computeTravellerAges = (travellers: Traveller[], returnDate: string): number[] => {
    return travellers.map((traveller) => {
        if (!traveller.birthday) return -1
        return calculateAge(traveller.birthday, returnDate)
    })
}

export const computeAdultChildCounts = (ages: number[]): AdultChildCounts | null => {
    if (ages.some((age) => age < 0)) return null
    return {
        adultCount: ages.filter(isAdultAge).length,
        childCount: ages.filter((age) => !isAdultAge(age)).length,
    }
}

const getEntryAdultChildCounts = (entry: SavedParticipantsEntry): AdultChildCounts => {
    if (typeof entry.adultCount === 'number' && typeof entry.childCount === 'number') {
        return { adultCount: entry.adultCount, childCount: entry.childCount }
    }
    const legacyAges = entry.ages ?? []
    return {
        adultCount: legacyAges.filter(isAdultAge).length,
        childCount: legacyAges.filter((age) => !isAdultAge(age)).length,
    }
}

const adultChildCountsMatch = (a: AdultChildCounts, b: AdultChildCounts): boolean =>
    a.adultCount === b.adultCount && a.childCount === b.childCount

/** Indices of travellers in order, split by adult vs child (booking order preserved within each group). */
export const splitTravellerIndicesByAdultChild = (
    ages: number[]
): { adultIndices: number[]; childIndices: number[] } => {
    const adultIndices: number[] = []
    const childIndices: number[] = []
    ages.forEach((age, index) => {
        if (isAdultAge(age)) adultIndices.push(index)
        else childIndices.push(index)
    })
    return { adultIndices, childIndices }
}

export const saveParticipantsToLocalStorage = (
    travellers: Traveller[],
    returnDate: string
): void => {
    if (!isStorageAvailable() || !travellers.length) return

    const ages = computeTravellerAges(travellers, returnDate)
    const counts = computeAdultChildCounts(ages)
    if (!counts) return

    const entry: SavedParticipantsEntry = {
        count: travellers.length,
        adultCount: counts.adultCount,
        childCount: counts.childCount,
        travellers: travellers.map((traveller) => ({
            firstName: (traveller.firstName || '').trim(),
            lastName: (traveller.lastName || '').trim(),
            birthday: traveller.birthday,
            gender: traveller.gender ?? null,
        })),
        updatedAt: new Date().toISOString(),
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
}

export const getMatchingParticipantsFromLocalStorage = (
    counts: AdultChildCounts
): SavedParticipantData[] | null => {
    if (!isStorageAvailable()) return null

    let entry: SavedParticipantsEntry
    try {
        entry = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as SavedParticipantsEntry
    } catch {
        return null
    }

    if (!entry?.travellers?.length) return null

    const daysSinceUpdate =
        (Date.now() - new Date(entry.updatedAt).getTime()) / (1000 * 60 * 60 * 24)
    if (daysSinceUpdate > TTL_DAYS) return null

    const entryCounts = getEntryAdultChildCounts(entry)
    if (!adultChildCountsMatch(entryCounts, counts)) return null
    if (entryCounts.adultCount + entryCounts.childCount !== entry.travellers.length) return null

    return entry.travellers
}
